import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/apiError";

/**
 * Handles SSO from clinq.africa.
 * Expects: /sso?sso_token=<jwt>&redirect=/new&prefill_company=Acme&prefill_sector=Fintech&...
 *
 * Flow:
 *   1. Exchange sso_token for a Clinq FMB session via POST /api/auth/sso
 *   2. Forward all prefill_* query params to the redirect target so the wizard can prefill
 *   3. Default redirect = /new (the wizard)
 */
export default function SSOCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = params.get("sso_token");
    const redirect = params.get("redirect") || "/dashboard";

    if (!token) {
      toast.error("Missing SSO token");
      navigate("/login");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/sso", { token });
        if (data.token) localStorage.setItem("session_token", data.token);
        setUser(data.user);

        // Forward prefill_* and utm_* params to the redirect target
        const forward = new URLSearchParams();
        params.forEach((v, k) => {
          if (k.startsWith("prefill_") || k.startsWith("utm_")) forward.set(k, v);
        });
        const qs = forward.toString();
        navigate(qs ? `${redirect}?${qs}` : redirect, { replace: true });
      } catch (err) {
        const msg = extractErrorMessage(err, "Single sign-on failed");
        toast.error(msg);
        navigate("/login");
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center text-stone-500 font-display" data-testid="sso-loading">
      Signing you in from clinq.africa…
    </div>
  );
}
